import {Crown} from 'lucide-react';
import {PropsWithChildren, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {MainLoader, cn} from '@chekin/guestapp-ui-kit';
import {GUEST_STATUSES, ShortGuest} from '@chekin/core';
import ChekinProSettings from 'ChekinProSettings';
import {appendLeaderParam} from 'utils/guestForm';
import {useLayoutMode} from 'hooks/useLayoutMode';
import {useGuestGroup, useReservationSettings} from 'hooks';
import {GuestItem} from 'components/GuestsPanel/components/GuestItem';
import {LoaderWrapper} from './styled';
import {RegisterButton} from '../styled';
import {NoGuests} from './NoGuests';

const EmptyGuest = {
  id: '',
  document_number: '',
  is_pre_registered: false,
  email: '',
  participant_id: '',
  full_name: '',
  guestapp_status: GUEST_STATUSES.incomplete,
  isLeader: false,
};

const PanelGroupTitle = ({
  children,
  className,
}: PropsWithChildren & {className?: string}) => {
  const isKiosko = useLayoutMode() === 'kiosko';
  return (
    <div
      className={cn(
        'w-full flex gap-2 items-center',
        'text-sm font-semibold leading-6 text-[var(--font-secondary)] uppercase',
        'panel-group-title',
        isKiosko && ' justify-center',
        className,
      )}
    >
      {children}
    </div>
  );
};

type Props = {
  handleRegisterNewGuest?: (isLeader?: boolean) => void;
  nextPage: string;
};
function GuestsList({handleRegisterNewGuest, nextPage}: Props) {
  const {guestGroup, isLoading, isFetching} = useGuestGroup();
  const hasNoGuests = guestGroup?.added_number_of_guests === 0;
  const {t} = useTranslation();
  const {checkCanShowRegisterButton} = useReservationSettings();
  const isRegisterGuestButtonVisible = checkCanShowRegisterButton();
  const members = guestGroup?.members;
  const numberOfGuests = guestGroup?.known_number_of_guests ?? 0;
  const showCoTravelers = numberOfGuests > 1;
  const isKiosko = useLayoutMode() === 'kiosko';
  const isNotSpecialRole = ChekinProSettings.isLeader === undefined;
  const isCoTravelerOnly = ChekinProSettings.isLeader === false;

  const {leaderGuest, coTravelers} = useMemo(() => {
    let leaderGuest: ShortGuest | undefined = undefined;
    const accompanyingMembers: ShortGuest[] = [];

    members?.forEach((member: ShortGuest) => {
      if (member.id === guestGroup?.leader_id) leaderGuest = member;
      else accompanyingMembers.push(member);
    });

    const coTravelers = Array.from(
      {length: Math.max(accompanyingMembers.length, numberOfGuests - 1)},
      (_, index) => accompanyingMembers[index] ?? EmptyGuest,
    );

    return {leaderGuest, coTravelers};
  }, [members, guestGroup, numberOfGuests]);

  const kioskoRegisterAsLeader = isCoTravelerOnly ? false : members?.length === 0;

  if (isLoading) {
    return (
      <LoaderWrapper className="kiosko:w-full">
        <MainLoader visible width={80} />
      </LoaderWrapper>
    );
  }

  if (isKiosko && hasNoGuests) {
    return <NoGuests loading={isFetching} nextPage={nextPage} />;
  }

  return (
    <>
      <div className="w-full space-y-4">
        <PanelGroupTitle className="guest-register-view__group-title">
          {t('guest_in_charge')}
          <Crown
            size="20"
            color="#FFD166"
            fill="#FFD166"
            className="top-[-1px] relative"
          />
        </PanelGroupTitle>

        <div
          className={cn(
            'grid gap-6',
            isKiosko ? 'grid-cols-[325px] justify-center' : 'w-full xl:grid-cols-2',
          )}
        >
          <GuestItem
            isLoading={isFetching}
            guestMember={leaderGuest ?? {...EmptyGuest, isLeader: true}}
            disabled={isCoTravelerOnly}
            onRegisterNew={
              isCoTravelerOnly ? undefined : () => handleRegisterNewGuest?.(true)
            }
          />
        </div>

        {showCoTravelers && (
          <>
            <PanelGroupTitle className="guest-register-view__group-title">
              {t('accompanying_guests')}
            </PanelGroupTitle>

            <div
              className={cn(
                'grid gap-6',
                isKiosko ? 'grid-cols-[325px] justify-center' : 'w-full xl:grid-cols-2',
              )}
            >
              {coTravelers.map((member, index) => (
                <GuestItem
                  isLoading={isFetching}
                  guestMember={{...member, isLeader: false}}
                  key={index}
                  onRegisterNew={() =>
                    handleRegisterNewGuest?.(
                      isNotSpecialRole ? members?.length === 0 : false,
                    )
                  }
                />
              ))}
            </div>
          </>
        )}
      </div>

      {isKiosko && isRegisterGuestButtonVisible && (
        <RegisterButton
          to={appendLeaderParam(nextPage, kioskoRegisterAsLeader)}
          loading={isFetching}
          onClick={() => handleRegisterNewGuest?.(kioskoRegisterAsLeader)}
          label={t('register_another_guest')}
          className={cn(
            'guest-register-view__register-btn',
            'kiosko:w-[698px] kiosko:max-w-full kiosko:h-[114px] kiosko:rounded-[56px] kiosko:mt-[24px]',
            'kiosko:[&_.buttonLabel]:text-kiosko-m kiosko:[&_.buttonLabel]:leading-normal',
          )}
        />
      )}
    </>
  );
}

export {GuestsList};
